/**
 * Sanitize a "next" path to avoid resuming deep, stateful flows after login.
 *
 * Rules:
 * - Only internal absolute paths are considered (must start with '/')
 * - Any path under '/auth', '/login', '/signup' is ignored (returns empty)
 * - Any deep path under '/app' is normalized to '/app' (e.g., '/app/equipment' -> '/app')
 * - Other paths are returned as-is (e.g., '/dashboard/…')
 */
export function sanitizeNextTarget(input: string): string {
  if (!input || typeof input !== 'string') return '';
  if (!input.startsWith('/')) return '';

  // Strip origin if any (defensive)
  let path = input;
  try {
    // In case a full URL slips in
    if (/^https?:\/\//i.test(input)) {
      const u = new URL(input);
      path = u.pathname + (u.search || '') + (u.hash || '');
    }
  } catch {
    /* noop */
  }

  const lower = path.toLowerCase();

  // Disallow auth-like destinations
  if (lower.startsWith('/auth') || lower.startsWith('/login') || lower.startsWith('/signup')) {
    return '';
  }

  // Normalize any deep app path to '/app'
  if (lower.startsWith('/app/')) {
    return '/app';
  }

  // '/app' itself is fine
  if (lower === '/app') return '/app';

  // Keep any other internal path (e.g., '/dashboard', '/dashboard/orders/status')
  return path;
}

/**
 * Decide the landing page after successful login.
 * Centralize logic to keep it maintainable; default to '/dashboard'.
 * Optionally use roleId/from to customize in the future.
 */
export function getPostLoginTarget(_roleId?: number | null, _from?: string): string {
  // For now, always land in dashboard to avoid deep app flows on first load.
  return '/dashboard';
}
